import $ from "jquery";
import ko from "lib/knockout";
import _ from "lib/underscore";
import {asNumber} from "../../../../core/utils/var";

/**
 * Base implementation of list that holds items of type {@link ListItem}
 */
export class BaseList {
	// fields
	_id = ko.observable('');
	_name = ko.observable('');
	_title = ko.observable('');
	_total = ko.observable(0);
	_items = ko.observable([]);

	_settings = ko.observable();

	// computed
	_indexById = ko.observable({});
	_indexByName = ko.observable({});

	_onItemClick = $.Callbacks();

	constructor(data = null) {
		this._id(data ? data.id : '');
		this._name(data ? data.name : '');
		this._title(data ? data.title : '');
		this._total(data ? data.total : 0);
		this._settings(data ? data.settings : null);

		// fix context for methods that are used in knockout bindings
		this.handleClick = this.handleClick.bind(this);
	}

	get id() {
		return this._id();
	}

	get name() {
		return this._name();
	}

	get title() {
		return this._title();
	}

	/** @return {ListItem[]} */
	get items() {
		return this._items();
	}

	/** @return {number} */
	get size() {
		return this.items.length;
	}

	/** @return {boolean} */
	get empty() {
		return this.size == 0;
	}

	/** @return {number} */
	get total() {
		return this._total();
	}

	/** @type {Settings} */
	get settings() {
		return this._settings();
	}

	/**
	 * Initializes the list with new data
	 * @param {Object|Object[]} data
	 */
	init(data) {
		if (_.isArray(data)) {
			this.initItems(data);
		} else if (_.isObject(data)) {
			this.initData(data);
		}
	}

	/**
	 * Initializes list properties
	 * @param {Object} data
	 */
	initData(data) {
		if (!_.isUndefined(data['id'])) {
			this._id(data['id']);
		}
		if (!_.isUndefined(data['name'])) {
			this._name(data['name']);
		}
		if (!_.isUndefined(data['title'])) {
			this._title(data['title']);
		}
		if (!_.isUndefined(data['total'])) {
			this._total(data['total']);
		}
		if (!_.isUndefined(data['settings'])) {
			this._settings(data['settings']);
		}
		if (_.isArray(data['items'])) {
			this.initItems(data['items']);
		}
	}

	/**
	 * Initializes list items
	 * @param {Object[]} itemsData
	 */
	initItems(itemsData) {
		const items = itemsData ? _.map(itemsData, this.newItem, this) : [];
		const indexById = itemsData ? _.indexBy(items, 'id') : {};
		const indexByName = itemsData ? _.indexBy(items, 'name') : {};
		this._items(items);
		this._indexById(indexById);
		this._indexByName(indexByName);
	}

	/**
	 * Creates ListItem from given data
	 * @param {Object} itemData
	 * @return {ListItem}
	 */
	newItem(itemData) {
		return new ListItem(itemData);
	}

	/**
	 * @param {number} id
	 * @return {ListItem}
	 */
	getItem(id) {
		return this._indexById()[id];
	}

	/**
	 * @param {string} name
	 * @return {ListItem}
	 */
	getItemByName(name) {
		return this._indexByName()[name];
	}

	/**
	 * Add event handler for item click
	 * @param {Function} callback
	 */
	onItemClick(callback) {
		this._onItemClick.add(callback);
	}

	/**
	 * @param {ListItem} item
	 */
	handleClick(item) {
		this._onItemClick.fire(item);
	}
}

/**
 * Implementation of BaseList that supports sorting and filtering
 */
export class List extends BaseList {
	// options
	_sortItems = ko.observable(true);
	_filterItems = ko.observable(true);

	constructor(data = null) {
		super(data);

		this._selectedItems = ko.pureComputed(() => {
			const items = _.filter(this.items, (item) => item.selected);
			return _.sortBy(items, (item) => item.selected);
		});
		this._unselectedItems = ko.pureComputed(() => {
			return _.filter(this.items, (item) => !item.selected)
		});
		this._sortedItems = ko.pureComputed(() => {
			return this.sortItems ? [...this.sortSource].sort(this.sortFunction.bind(this)) : this.sortSource;
		});
		this._filteredItems = ko.pureComputed(() => {
			return this.filterItems ? _.filter(this.filterSource, item => this.filterPredicate(item)) : this.filterSource;
		});

		/*
		visibleItems <- _sortedItems <- _filteredItems <- items
		 */
	}

	/** @type {boolean} */
	get sortItems() {
		return this._sortItems();
	}

	/** @param {boolean} value */
	set sortItems(value) {
		this._sortItems(value);
	}

	/** @type {boolean} */
	get filterItems() {
		return this._filterItems();
	}

	/** @param {boolean} value */
	set filterItems(value) {
		this._filterItems(value);
	}

	/**
	 * @return {ListItem[]}
	 */
	get selectedItems() {
		return this._selectedItems();
	}

	/**
	 * @return {ListItem[]}
	 */
	get unselectedItems() {
		return this._unselectedItems();
	}

	/**
	 * @return {ListItem[]}
	 */
	get sortSource() {
		return this._filteredItems();
	}

	/**
	 * @return {ListItem[]}
	 */
	get filterSource() {
		return this.items;
	}

	/**
	 * @param {ListItem} item1
	 * @param {ListItem} item2
	 * @return {number}
	 */
	sortFunction(item1, item2) {
		// by count descending
		return item2.count - item1.count;
	}

	/**
	 * @param {ListItem} item
	 * @return {boolean}
	 */
	filterPredicate(item) {
		// hide empty items
		return item.count > 0;
	}

	/**
	 * These items should be shown to user
	 * @return {ListItem[]}
	 */
	get visibleItems() {
		return this._sortedItems();
	}

	/**
	 * @return {number}
	 */
	get visibleCount() {
		return this.visibleItems.length;
	}

	/**
	 * @return {number}
	 */
	get hiddenCount() {
		return this.items.length - this.visibleItems.length;
	}

	/**
	 * @return {boolean}
	 */
	get hasVisibleItems() {
		return this.visibleItems.length > 0;
	}

	get size() {
		return this.visibleItems.length;
	}

	updateCounts(data) {
		_.each(this.items, item => {
			const d = data ? data[item.id] : null;
			item.count = asNumber(_.isObject(d) ? d.count : d);
		});
	}

	resetCounts() {
		_.each(this.items, item => item.count = 0)
	}
}

/**
 * Base implementation of list item for {@link List} class.
 * All input data can be observables to track changes.
 */
export class ListItem {
	_count = ko.observable(0);

	constructor(data) {
		this._data = {...data};
		this._count(asNumber(data.count));
	}

	get id() {
		return +this._data.id;
	}

	get name() {
		return '' + this._data.name;
	}

	get title() {
		return '' + this._data.title;
	}

	get count() {
		return this._count();
	}

	set count(value) {
		this._count(asNumber(value));
	}

	get selected() {
		return !!this._data.selected;
	}

	get disabled() {
		return !!this._data.disabled;
	}

	get empty() {
		return this.count == 0;
	}
}



// WEBPACK FOOTER //
// ./modules/shop/common/model/lists.js